import React, { useEffect, useState } from 'react';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Loader } from 'lucide-react';
import { api, errorMessage } from '../lib/api';
import { clearUrlSecret, readUrlSecret } from '../lib/urlSecrets';
import { useAuth } from '../context/AuthContext';
import { flag } from '../config';
import lazyAccountPage from '../components/site/auth/lazyAccountPage';

// SLOT_MODEL on: the redesigned page (site redesign Phase 6), loaded on demand.
const VerifyEmailSite = lazyAccountPage(
  'VerifyEmailSite',
  () => import(/* webpackChunkName: "account-pages" */ './account/VerifyEmailSite'),
);

/** /verify-email: the redesigned page with SLOT_MODEL on, the legacy V5 page (unchanged) off. */
export default function VerifyEmail() {
  return flag('SLOT_MODEL') ? <VerifyEmailSite /> : <VerifyEmailLegacy />;
}

// V5 Design Constants
const v5 = {
  colors: {
    navy: '#1e1b4b',
    cream: '#FEF7ED',
    white: '#FFFFFF',
    purple: '#8b5cf6',
    purpleDark: '#7c3aed',
    green: '#22c55e',
    red: '#ef4444',
    teal: '#14b8a6',
    textPrimary: '#1e1b4b',
    textSecondary: 'rgba(30, 27, 75, 0.7)',
    textMuted: 'rgba(30, 27, 75, 0.5)',
    textHint: 'rgba(30, 27, 75, 0.4)',
    borderLight: 'rgba(30, 27, 75, 0.05)',
  },
  fonts: {
    display: "'Faustina', Georgia, serif",
    body: "'DM Sans', sans-serif",
    mono: "'JetBrains Mono', monospace"
  }
};

function VerifyEmailLegacy() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  // In the URL, or kept for this page by the index.html URL secret guard (lib/urlSecrets).
  const [keptToken] = useState(() => readUrlSecret('token', { location }));
  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error', 'already_verified'
  const [message, setMessage] = useState('');

  useEffect(() => {
    const token = searchParams.get('token') || keptToken;

    if (!token) {
      setStatus('error');
      setMessage('Invalid verification link');
      return;
    }

    // Call verification endpoint
    verifyEmail(token);
  }, [searchParams]);

  const verifyEmail = async (token) => {
    try {
      const { data } = await api.post('/auth/verify-email', { token });
      clearUrlSecret('token');

      if (data.already_verified) {
        setStatus('already_verified');
        setMessage('Your email was already verified!');
      } else {
        setStatus('success');
        setMessage(data.message || 'Email verified successfully!');
      }

      // Refresh user data (fire-and-forget — don't let this disrupt the success UI)
      refreshUser().catch(() => {});

      // Redirect to profile after 3 seconds
      setTimeout(() => {
        navigate('/profile');
      }, 3000);
    } catch (error) {
      // A non-2xx now lands here, so the API's own detail is the message.
      setStatus('error');
      setMessage(errorMessage(error, 'Verification failed. The link may have expired.'));
    }
  };

  const getStatusConfig = () => {
    switch (status) {
      case 'verifying':
        return {
          icon: <Loader className="w-16 h-16 animate-spin" style={{ color: v5.colors.purple }} />,
          borderColor: v5.colors.purple,
          iconBg: 'rgba(139, 92, 246, 0.1)',
          title: 'Verifying Your Email',
          codeAccent: '// verifying_email'
        };
      case 'success':
        return {
          icon: <CheckCircle className="w-16 h-16" style={{ color: v5.colors.green }} />,
          borderColor: v5.colors.green,
          iconBg: 'rgba(34, 197, 94, 0.1)',
          title: 'Email Verified!',
          codeAccent: '// email_verified'
        };
      case 'already_verified':
        return {
          icon: <CheckCircle className="w-16 h-16" style={{ color: v5.colors.teal }} />,
          borderColor: v5.colors.teal,
          iconBg: 'rgba(20, 184, 166, 0.1)',
          title: 'Already Verified',
          codeAccent: '// already_verified'
        };
      case 'error':
        return {
          icon: <XCircle className="w-16 h-16" style={{ color: v5.colors.red }} />,
          borderColor: v5.colors.red,
          iconBg: 'rgba(239, 68, 68, 0.1)',
          title: 'Verification Failed',
          codeAccent: '// verification_error'
        };
      default:
        return {};
    }
  };

  const config = getStatusConfig();

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-20"
      style={{ backgroundColor: v5.colors.cream }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="rounded-xl p-8 md:p-12 max-w-md w-full text-center"
        style={{
          backgroundColor: v5.colors.white,
          border: `1px solid ${v5.colors.borderLight}`,
          boxShadow: '0 2px 4px rgba(30, 27, 75, 0.04)',
          borderLeft: `4px solid ${config.borderColor}`
        }}
      >
        {/* Code Accent */}
        <p
          className="mb-4"
          style={{
            fontFamily: v5.fonts.mono,
            fontSize: '12px',
            color: v5.colors.textHint
          }}
        >
          {config.codeAccent}
        </p>

        {status === 'verifying' && (
          <div>
            <div
              className="w-20 h-20 rounded-xl flex items-center justify-center mx-auto mb-6"
              style={{ backgroundColor: config.iconBg }}
            >
              {config.icon}
            </div>
            <h2
              className="text-2xl mb-4"
              style={{ fontFamily: v5.fonts.display, fontWeight: 500, color: v5.colors.textPrimary }}
            >
              {config.title}
            </h2>
            <p style={{ fontFamily: v5.fonts.body, color: v5.colors.textMuted }}>
              Please wait while we verify your email address...
            </p>
          </div>
        )}

        {status === 'success' && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 15 }}
          >
            <div
              className="w-20 h-20 rounded-xl flex items-center justify-center mx-auto mb-6"
              style={{ backgroundColor: config.iconBg }}
            >
              {config.icon}
            </div>
            <h2
              className="text-2xl mb-4"
              style={{ fontFamily: v5.fonts.display, fontWeight: 500, color: v5.colors.textPrimary }}
            >
              {config.title}
            </h2>
            <p
              className="mb-6"
              style={{ fontFamily: v5.fonts.body, color: v5.colors.textMuted }}
            >
              {message}
            </p>
            <p
              className="text-sm"
              style={{ fontFamily: v5.fonts.body, color: v5.colors.textMuted }}
            >
              Check your inbox for a welcome email with local deals near you!
            </p>
            <p
              className="text-sm mt-4"
              style={{ fontFamily: v5.fonts.body, color: v5.colors.textHint }}
            >
              Redirecting to your profile...
            </p>
          </motion.div>
        )}

        {status === 'already_verified' && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 15 }}
          >
            <div
              className="w-20 h-20 rounded-xl flex items-center justify-center mx-auto mb-6"
              style={{ backgroundColor: config.iconBg }}
            >
              {config.icon}
            </div>
            <h2
              className="text-2xl mb-4"
              style={{ fontFamily: v5.fonts.display, fontWeight: 500, color: v5.colors.textPrimary }}
            >
              {config.title}
            </h2>
            <p
              className="mb-6"
              style={{ fontFamily: v5.fonts.body, color: v5.colors.textMuted }}
            >
              {message}
            </p>
            <p
              className="text-sm mt-4"
              style={{ fontFamily: v5.fonts.body, color: v5.colors.textHint }}
            >
              Redirecting to your profile...
            </p>
          </motion.div>
        )}

        {status === 'error' && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 15 }}
          >
            <div
              className="w-20 h-20 rounded-xl flex items-center justify-center mx-auto mb-6"
              style={{ backgroundColor: config.iconBg }}
            >
              {config.icon}
            </div>
            <h2
              className="text-2xl mb-4"
              style={{ fontFamily: v5.fonts.display, fontWeight: 500, color: v5.colors.textPrimary }}
            >
              {config.title}
            </h2>
            <p
              className="mb-6"
              style={{ fontFamily: v5.fonts.body, color: v5.colors.textMuted }}
            >
              {message}
            </p>
            <button
              onClick={() => navigate('/profile')}
              className="px-8 py-3 rounded-xl font-semibold text-white transition-all hover:-translate-y-0.5"
              style={{
                background: `linear-gradient(135deg, ${v5.colors.purple} 0%, ${v5.colors.purpleDark} 100%)`,
                fontFamily: v5.fonts.body,
                boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4)'
              }}
            >
              Go to Profile
            </button>
            <p
              className="text-sm mt-4"
              style={{ fontFamily: v5.fonts.body, color: v5.colors.textMuted }}
            >
              You can request a new verification link from your profile page.
            </p>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
}
